import { ExternalLink } from "lucide-react";
import { DashCard, DashHeading, SectionTitle } from "@/components/dashboard";

// ────────────────────────────────────────────────────────────────────────────
// CONTENIDO EDITABLE — reemplazar por los IDs / URL reales cuando estén listos 
// ────────────────────────────────────────────────────────────────────────────

type Video = {
  title: string;
  description: string;
  youtubeId: string;   // ID de 11 caracteres, la parte después de "watch?v=" en YouTube
  duration?: string;   // ej: "3:45" — opcional pero recomendado
};

const VIDEOS: Video[] = [
  {
    title: "Generación de códigos",
    description: "Genera códigos para dar acceso a la App NiuFit.",
    youtubeId: "ccKItQAA0JY",
    duration: "2 min",
  },
  {
    title: "Explicación general de la plataforma",
    description: "Un recorrido por las diferentes partes de la plataforma.",
    youtubeId: "l7n0AWMDF38",
    duration: "3 min",
  },
  {
    title: "Cómo canjear un código en la app",
    description: "Paso a paso para acompañar al cliente en su primera activación.",
    youtubeId: "hSbkgiPg8qE",
    duration: "1 min",
  },
];

// URL del Google Doc — debe estar publicado a la web:
//   File → Share → Publish to web → Publish → copiar la URL que termina en /pub
// Para embeber, se agrega ?embedded=true
const GUIDE_EMBED_URL = "https://docs.google.com/document/d/e/2PACX-1vQxIhL-otVYxgljnKn4J3r5iygA-PjRKGmYUSrY5UzyPUDniMz_inh36mJ7LmlCEA/pub?embedded=true";
// URL de solo lectura para abrir en pestaña nueva (mismo /pub sin ?embedded=true)
const GUIDE_OPEN_URL = "https://docs.google.com/document/d/e/2PACX-1vQxIhL-otVYxgljnKn4J3r5iygA-PjRKGmYUSrY5UzyPUDniMz_inh36mJ7LmlCEA/pub";

// ────────────────────────────────────────────────────────────────────────────

export default function TutorialsPage() {
  return (
    <div>
      <DashHeading
        title="Tutoriales"
        description="Recursos para el equipo del partner. Videos cortos y una guía escrita sobre qué es NiuFit, cuándo ofrecerlo al cliente y cómo presentarlo."
      />

      {/* Videos */}
      <div className="mb-10">
        <SectionTitle hint={`${VIDEOS.length} videos`}>Videos</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {VIDEOS.map((v) => (
            <VideoCard key={v.youtubeId} video={v} />
          ))}
        </div>
      </div>

      {/* Guía escrita */}
      <div className="mb-10">
        <SectionTitle>Guía para recepcionistas</SectionTitle>
        <DashCard
          title="Cómo presentar y vender NiuFit"
          action={
            <a
              href={GUIDE_OPEN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
            >
              Abrir en Google Docs
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={2} />
            </a>
          }
          bodyClassName="p-0"
        >
          <iframe
            src={GUIDE_EMBED_URL}
            title="Guía para recepcionistas"
            className="w-full border-0"
            style={{ height: "80vh", minHeight: "600px" }}
            loading="lazy"
          />
        </DashCard>
      </div>
    </div>
  );
}

function VideoCard({ video }: { video: Video }) {
  const embedUrl = `https://www.youtube.com/embed/${video.youtubeId}`;
  const watchUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;

  return (
    <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
      <div className="relative w-full bg-neutral-100" style={{ aspectRatio: "16/9" }}>
        <iframe
          src={embedUrl}
          title={video.title}
          className="absolute inset-0 w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
        />
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-semibold text-neutral-900 leading-tight">
            {video.title}
          </h3>
          {video.duration && (
            <span className="text-xs text-neutral-500 font-mono-num tabular-nums flex-shrink-0">
              {video.duration}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-600 leading-relaxed mb-3">
          {video.description}
        </p>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
        >
          Abrir en YouTube
          <ExternalLink className="h-3 w-3" strokeWidth={2} />
        </a>
      </div>
    </div>
  );
}
